import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemAvatar,
  Avatar,
  Chip,
  Paper,
  Alert,
  Divider,
  IconButton,
  Fade,
  Confetti,
  Backdrop,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import ResponsiveDataGrid from '../components/ResponsiveDataGrid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  EmojiEvents,
  Add,
  Casino,
  AccountBalance,
  Person,
  CalendarToday,
  Shuffle,
  History,
  TrendingUp,
  Star,
  CardGiftcard,
  Celebration,
  CurrencyRupee,
  Schedule,
  Group,
} from '@mui/icons-material';
import axios from 'axios';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { formatINR } from '../utils/currency';
import { useAuth } from '../contexts/AuthContext';
import EmptyState from '../components/EmptyState';
import PageHeader from '../components/PageHeader';
import MemberDetailDialog from '../components/MemberDetailDialog';
import StatCard from '../components/StatCard';
import { ACCENTS } from '../theme/accents';
import { useDistribution } from '../hooks/useMandalName';

// DataGrid renders this instead of a bare "No rows" line.
const NoRows = () => (
  <EmptyState
    icon={<EmojiEvents />}
    accent={ACCENTS.silver}
    title={'No distributions yet'}
    description={'Once a draw is made, every recipient, amount and date is listed here so the rotation stays auditable.'}
  />
);

const Distribution = () => {
  const { user, isAdmin } = useAuth();
  const { title, lower } = useDistribution();
  // Months between draws; 0 means the group keeps no fixed schedule
  const [intervalMonths, setIntervalMonths] = useState(null);
  const [distributions, setDistributions] = useState([]);
  const [eligibleMembers, setEligibleMembers] = useState([]);
  const [rotation, setRotation] = useState(null);
  const [monthCollected, setMonthCollected] = useState(0);
  const [selectedMember, setSelectedMember] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailMemberId, setDetailMemberId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [randomSelectionOpen, setRandomSelectionOpen] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [randomizing, setRandomizing] = useState(false);
  const [stats, setStats] = useState({
    totalDistributions: 0,
    totalAmount: 0,
    lastDistribution: null,
    nextDistribution: null,
  });
  const [formData, setFormData] = useState({
    recipientUserId: '',
    amount: 0,
    distributionDate: dayjs(),
    distributionMonth: dayjs().format('YYYY-MM'),
    notes: '',
  });

  useEffect(() => {
    fetchDistributions();
    fetchEligibleMembers();
    calculateStats();
  }, []);

  useEffect(() => {
    calculateStats();
  }, [distributions, intervalMonths]);

  const fetchDistributions = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/distributions');
      setDistributions(response.data);
    } catch (error) {
      toast.error('Failed to fetch distributions');
    } finally {
      setLoading(false);
    }
  };

  const fetchEligibleMembers = async () => {
    try {
      // Eligible = still awaiting their turn in the current rotation
      const [eligibleRes, rotationRes, statsRes, settingsRes] = await Promise.all([
        axios.get('/api/distributions/eligible-members'),
        axios.get('/api/distributions/rotation'),
        axios.get('/api/dashboard/stats'),
        axios.get('/api/rules/settings'),
      ]);
      const interval = settingsRes.data?.silverDistributionMonths;
      setIntervalMonths(interval === undefined ? 2 : Number(interval));
      setEligibleMembers(eligibleRes.data);
      setRotation(rotationRes.data);
      // The distribution is worth what was actually collected this month, not a
      // flat per-head guess
      setMonthCollected(Number(statsRes.data?.currentMonth?.collected) || 0);
    } catch (error) {
      console.error('Failed to fetch eligible members');
    }
  };

  const calculateStats = () => {
    const totalDistributions = distributions.length;
    const totalAmount = distributions.reduce((sum, d) => sum + parseFloat(d.amount), 0);
    const lastDistribution = distributions.length > 0 ? distributions[0] : null;
    
    // The next draw follows the group's own interval (Admin Panel > System Settings)
    let nextDistribution = null;
    if (intervalMonths > 0) {
      nextDistribution = lastDistribution
        ? dayjs(lastDistribution.distribution_date).add(intervalMonths, 'months')
        : dayjs().add(intervalMonths, 'months').startOf('month');
    }

    setStats({
      totalDistributions,
      totalAmount,
      lastDistribution,
      nextDistribution,
    });
  };

  const handleRandomSelection = async () => {
    setRandomizing(true);
    setRandomSelectionOpen(true);
    
    try {
      // Add some suspense with a delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const response = await axios.get('/api/distributions/random-selection');
      setSelectedMember(response.data);
      setShowConfetti(true);
      
      // Calculate amount based on current month's total collection
      const currentMonth = dayjs().format('YYYY-MM');
      const monthlyAmount = monthCollected;
      
      setFormData({
        recipientUserId: response.data.id,
        amount: monthlyAmount,
        distributionDate: dayjs(),
        distributionMonth: currentMonth,
        notes: `Random selection for ${currentMonth} distribution`,
      });
      
      toast.success(`🎉 ${response.data.full_name} has been randomly selected!`);
      
      // Hide confetti after 3 seconds
      setTimeout(() => setShowConfetti(false), 3000);
      
    } catch (error) {
      toast.error('Failed to select random member');
    } finally {
      setRandomizing(false);
    }
  };

  const handleCreateDistribution = async () => {
    try {
      await axios.post('/api/distributions', {
        ...formData,
        distributionDate: formData.distributionDate.format('YYYY-MM-DD'),
      });
      
      toast.success(`${title} recorded successfully! 🎉`);
      setDialogOpen(false);
      setRandomSelectionOpen(false);
      setSelectedMember(null);
      setFormData({
        recipientUserId: '',
        amount: 0,
        distributionDate: dayjs(),
        distributionMonth: dayjs().format('YYYY-MM'),
        notes: '',
      });
      fetchDistributions();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to record distribution');
    }
  };

  const handleConfirmDistribution = () => {
    setRandomSelectionOpen(false);
    setDialogOpen(true);
  };

  const columns = [
    { 
      field: 'id', 
      headerName: 'ID', 
      width: 60,
      renderCell: (params) => (
        <Typography variant="caption" color="textSecondary">
          #{params.value}
        </Typography>
      ),
    },
    { 
      field: 'recipient_name', 
      headerName: 'Recipient', 
      width: 180,
      renderCell: (params) => (
        <Box display="flex" alignItems="center">
          <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: 'warning.main' }}>
            <EmojiEvents fontSize="small" />
          </Avatar>
          <Typography variant="body2" fontWeight="medium">
            {params.value}
          </Typography>
        </Box>
      ),
    },
    { 
      field: 'amount', 
      headerName: 'Amount', 
      width: 120,
      renderCell: (params) => (
        <Box display="flex" alignItems="center">
          <CurrencyRupee fontSize="small" sx={{ mr: 0.5, color: 'success.main' }} />
          <Typography variant="body2" fontWeight="bold" color="success.main">
            {formatINR(params.value)}
          </Typography>
        </Box>
      ),
    },
    { 
      field: 'distribution_date', 
      headerName: 'Distribution Date', 
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2">
          {dayjs(params.value).format('MMM D, YYYY')}
        </Typography>
      ),
    },
    { 
      field: 'distribution_month', 
      headerName: 'Period', 
      width: 100,
      renderCell: (params) => (
        params.value ? dayjs(`${params.value}-01`).format('MMM YYYY') : '—'
      ),
    },
    { 
      field: 'notes', 
      headerName: 'Notes', 
      width: 200,
      renderCell: (params) => (
        <Typography variant="body2" color="textSecondary">
          {params.value || 'No notes'}
        </Typography>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        eyebrow="Rotation"
        accent={ACCENTS.magenta}
        title={title}
        subtitle="One member is drawn each cycle. Nobody is drawn twice until everyone has had a turn."
        actions={isAdmin && (
          <>
            <Button variant="contained" startIcon={<Casino />} onClick={handleRandomSelection}>
              Random Selection
            </Button>
            <Button variant="outlined" startIcon={<Add />} onClick={() => setDialogOpen(true)}>
              Manual Distribution
            </Button>
          </>
        )}
      />

      {/* The rotation, made visible - the draw only picks from members who have
          not yet had a turn this cycle, so everyone receives before anyone repeats */}
      {rotation && rotation.totalMembers > 0 && (
        <Paper sx={{ p: 2.5, mb: 3, border: '1px solid', borderColor: 'divider' }}>
          <Box display="flex" alignItems="baseline" justifyContent="space-between" flexWrap="wrap" gap={1} mb={1.5}>
            <Typography variant="overline">
              Rotation — round {rotation.round}
            </Typography>
            <Typography variant="body2" color="text.secondary" className="tabular">
              {rotation.receivedThisCycle} of {rotation.totalMembers} have received
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={(rotation.receivedThisCycle / rotation.totalMembers) * 100}
            sx={{ height: 8, borderRadius: 999, mb: 2 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {rotation.awaiting.length === rotation.totalMembers
              ? 'Everyone is eligible — this is a fresh cycle.'
              : `Still awaiting a turn: ${rotation.awaiting.join(', ')}`}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            The draw only picks from members awaiting a turn, so nobody receives twice
            before everyone has received once.
          </Typography>
        </Paper>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={2.5} mb={3}>
        <Grid item xs={6} md={3}>
          <StatCard title="Draws" value={stats.totalDistributions} subtitle="All time"
            icon={<TrendingUp />} accent={ACCENTS.magenta} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard title="Distributed" value={formatINR(stats.totalAmount)} subtitle="Paid out"
            icon={<CurrencyRupee />} accent={ACCENTS.emerald} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            title="Last draw"
            value={stats.lastDistribution
              ? dayjs(stats.lastDistribution.distribution_date).format('MMM YYYY')
              : '—'}
            subtitle="Most recent"
            icon={<History />}
            accent={ACCENTS.silver}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard title="Next draw" value={stats.nextDistribution?.format('MMM YYYY') || '—'}
            subtitle={intervalMonths === 0 ? 'No fixed schedule' : 'On the schedule'} icon={<Schedule />} accent={ACCENTS.teal} />
        </Grid>
      </Grid>

      {/* Distribution History */}
      <Paper sx={{ width: '100%' }}>
        <ResponsiveDataGrid
          mobile={{ title: 'recipient_name', amount: 'amount', hide: ['id'] }}
          rows={distributions}
          columns={columns}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          pageSizeOptions={[10, 25, 50]}
          checkboxSelection={false}
          disableRowSelectionOnClick
          onRowClick={(params) => setDetailMemberId(params.row.recipient_user_id)}
          autoHeight
          loading={loading}
          slots={{ noRowsOverlay: NoRows }}
          sx={{
            '& .MuiDataGrid-row': { cursor: 'pointer' },
            // v6 collapses the no-rows overlay under autoHeight, clipping it
            '& .MuiDataGrid-overlayWrapper': { minHeight: 300 },
            '& .MuiDataGrid-overlayWrapperInner': { minHeight: 300 },
            '& .MuiDataGrid-cell:hover': { color: 'primary.main' },
          }}
        />
      </Paper>

      {/* Random Selection Dialog */}
      <Dialog 
        open={randomSelectionOpen} 
        onClose={() => !randomizing && setRandomSelectionOpen(false)}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Casino sx={{ mr: 1 }} />
            Random Member Selection
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" alignItems="center" py={3}>
            {randomizing ? (
              <Box display="flex" flexDirection="column" alignItems="center">
                <CircularProgress size={60} sx={{ mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Randomly selecting member...
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  🎲 Rolling the dice...
                </Typography>
              </Box>
            ) : selectedMember ? (
              <Fade in={!randomizing}>
                <Box display="flex" flexDirection="column" alignItems="center">
                  <Avatar 
                    sx={{ 
                      width: 100, 
                      height: 100, 
                      bgcolor: 'warning.main',
                      fontSize: '2.5rem',
                      mb: 2 
                    }}
                  >
                    {selectedMember.full_name.charAt(0)}
                  </Avatar>
                  <Typography variant="h4" gutterBottom fontWeight="bold">
                    🎉 {selectedMember.full_name}
                  </Typography>
                  <Typography variant="h6" color="success.main" gutterBottom>
                    Congratulations!
                  </Typography>
                  <Typography variant="body1" textAlign="center" sx={{ mb: 2 }}>
                    You have been randomly selected for this month's {lower}!
                  </Typography>
                  <Chip 
                    icon={<EmojiEvents />}
                    label="WINNER"
                    color="warning"
                    sx={{ fontSize: '1rem', py: 1 }}
                  />
                </Box>
              </Fade>
            ) : null}
          </Box>
        </DialogContent>
        {selectedMember && !randomizing && (
          <DialogActions>
            <Button onClick={() => setRandomSelectionOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmDistribution} 
              variant="contained"
              startIcon={<CardGiftcard />}
            >
              Proceed with Distribution
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* Manual Distribution Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record {title}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Recipient"
                value={formData.recipientUserId}
                onChange={(e) => setFormData({ ...formData, recipientUserId: e.target.value })}
                required
              >
                {eligibleMembers.map((member) => (
                  <MenuItem key={member.id} value={member.id}>
                    {member.full_name} (@{member.username})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                required
                InputProps={{
                  startAdornment: '₹',
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Distribution Month"
                value={formData.distributionMonth}
                onChange={(e) => setFormData({ ...formData, distributionMonth: e.target.value })}
                placeholder="YYYY-MM"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <DatePicker
                label="Distribution Date"
                value={formData.distributionDate}
                onChange={(newValue) => setFormData({ ...formData, distributionDate: newValue })}
                slotProps={{ textField: { fullWidth: true, required: true } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes about this distribution..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateDistribution} variant="contained">
            Record Distribution
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confetti Effect */}
      {showConfetti && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            zIndex: 9999,
          }}
        >
          {/* This would normally use a confetti library like react-confetti */}
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              fontSize: '4rem',
              animation: 'bounce 1s infinite',
              '@keyframes bounce': {
                '0%, 100%': { transform: 'translate(-50%, -50%) scale(1)' },
                '50%': { transform: 'translate(-50%, -50%) scale(1.2)' },
              },
            }}
          >
            🎉✨🎊
          </Box>
        </Box>
      )}
      <MemberDetailDialog
        memberId={detailMemberId}
        open={Boolean(detailMemberId)}
        onClose={() => setDetailMemberId(null)}
      />

    </Box>
  );
};

export default Distribution;